const BASE = '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...options?.headers,
      ...(options?.body && typeof options.body === 'string'
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
  });

  const data = await res.json() as any;

  if (!res.ok) {
    throw new Error(data.error || `Request failed: ${res.status}`);
  }

  return data as T;
}

export const api = {
  // Public
  getSections: () => request<any>('/sections'),
  getSection: (id: string) => request<any>(`/sections/${id}`),
  getArticle: (id: string) => request<any>(`/articles/${id}`),
  search: (q: string, app?: string | null) => {
    const params = new URLSearchParams({ q });
    if (app) params.set('app', app);
    return request<any>(`/search?${params}`);
  },

  // Admin auth
  login: (email: string, password: string) =>
    request<any>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<any>('/admin/logout', { method: 'POST' }),
  getSession: () => request<any>('/admin/session'),

  // Admin articles
  getAdminArticles: (app?: string, sectionId?: string) => {
    const params = new URLSearchParams();
    if (app) params.set('app', app);
    if (sectionId) params.set('section_id', sectionId);
    return request<any>(`/admin/articles?${params}`);
  },
  createArticle: (data: any) =>
    request<any>('/admin/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateArticle: (id: string, data: any) =>
    request<any>(`/admin/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteArticle: (id: string) =>
    request<any>(`/admin/articles/${id}`, { method: 'DELETE' }),
  togglePublish: (id: string) =>
    request<any>(`/admin/articles/${id}/publish`, { method: 'POST' }),

  // Admin sections
  getAdminSections: () => request<any>('/admin/sections'),
  createSection: (data: any) =>
    request<any>('/admin/sections', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSection: (id: string, data: any) =>
    request<any>(`/admin/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteSection: (id: string) =>
    request<any>(`/admin/sections/${id}`, { method: 'DELETE' }),

  // Admin media
  getAdminMedia: (articleId?: string) => {
    const params = new URLSearchParams();
    if (articleId) params.set('article_id', articleId);
    return request<any>(`/admin/media?${params}`);
  },
  // Uses XMLHttpRequest (not fetch) so we can report upload progress and surface
  // real errors — including Cloudflare's 413 when the file exceeds the 100MB
  // request-body limit, which returns HTML rather than JSON.
  uploadMedia: (formData: FormData, onProgress?: (pct: number) => void) =>
    new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}/admin/media/upload`);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        let data: any = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          // Non-JSON response (e.g. Cloudflare 413 HTML error page)
        }

        if (xhr.status >= 200 && xhr.status < 300 && data?.success) {
          resolve(data);
        } else if (xhr.status === 413) {
          reject(
            new Error(
              'File is too large to upload through the server (100MB limit). Upload it directly to the R2 bucket instead.'
            )
          );
        } else {
          reject(new Error(data?.error || `Upload failed (HTTP ${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));

      xhr.send(formData);
    }),
  deleteMedia: (id: string) =>
    request<any>(`/admin/media/${id}`, { method: 'DELETE' }),
};
